import React, { useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { Box, Card, Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import type { MRT_ColumnDef, MRT_PaginationState, MRT_Row } from 'material-react-table';
import { useSnackbar } from 'notistack';

import { triggerDownload } from 'common/fileUtils';
import { useHandleExpiredSession, usePersistentState } from 'common/hooks';

import { ActionButton } from 'commonComponents/ActionButton';
import { ClaimLicense } from 'commonComponents/ClaimLicense';
import { LinkButton } from 'commonComponents/LinkButton';
import { CellWithEllipsis, mapBoolean, MaterialTable } from 'commonComponents/MaterialTable';
import { PageContainer } from 'commonComponents/PageContainer';
import { SelectProduct } from 'commonComponents/SelectProduct';

import { useUser } from 'api/authorization';
import { type CustomerDetails, downloadCustomerOrigins, loadCustomerList } from 'api/customers';
import { useCurrentProduct } from 'api/products';
import { CreateIcon } from 'icons/CreateIcon';
import { ExcelIcon } from 'icons/ExcelIcon';
import { ROLE_DEFINITIONS } from 'roles/definitions';
import { RoleDependentComponent } from 'roles/RoleDependentComponent';
import { ROUTES } from 'routes/routes';

let columns: Array<MRT_ColumnDef<CustomerDetails>> = [
    { header: 'Name', accessorKey: 'full_name' },
    { header: 'Email', accessorKey: 'email' },
    { header: 'Company', accessorKey: 'company' },
    { header: 'Default Owner ID', accessorKey: 'owner_id' },
    { header: 'Active', accessorFn: customer => mapBoolean(customer.is_active) },
    { header: 'Comment', accessorKey: 'comment', Cell: CellWithEllipsis, minSize: 340 },
];
columns = columns.map(c => ({ ...c, id: c.header }));

export function CustomerList() {
    const navigate = useNavigate();
    const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 100 });
    const { data: currentProduct } = useCurrentProduct();
    const { data: user } = useUser();
    const userHasProduct = Boolean(user?.products?.length);
    const productIsSelected = Boolean(currentProduct);

    const { data, isInitialLoading } = useQuery({
        enabled: Boolean(currentProduct),
        queryKey: ['Customer', 'list', { product: currentProduct?.id }],
        queryFn: () => loadCustomerList({ product: currentProduct?.id }),
        select: users => users.map(u => ({ ...u, strikethrough: u.invalidated })),
        meta: { errorMessage: 'Loading customers failed' },
    });

    const defaultSort = [{ id: 'Name', desc: false }];
    const showDetails = (row: MRT_Row<CustomerDetails>) =>
        navigate(generatePath(ROUTES.CUSTOMER_DETAIL, { id: row.original.id.toString() }));

    const [hideInternalCustomers, setHideInternalCustomers] = usePersistentState(
        'customerHideInternalCustomers',
        false,
        false
    );
    const Filters = (
        <CustomerListFilters
            hideInternalCustomers={hideInternalCustomers}
            setHideInternalCustomers={setHideInternalCustomers}
        />
    );

    const filteredData = useMemo(() => {
        return (data || []).filter(c => (hideInternalCustomers ? !c.is_internal : true));
    }, [data, hideInternalCustomers]);

    return (
        <PageContainer dataTestId="CustomerList_Container" maxWidth={false} maxHeight="100%">
            {!userHasProduct && <ClaimLicense />}
            {userHasProduct && !productIsSelected && <SelectProduct />}
            {userHasProduct && productIsSelected && (
                <>
                    <Card sx={{ padding: 1, marginBottom: 1, overflow: 'inherit' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <CustomerListButtons />
                        </Box>
                    </Card>
                    <MaterialTable
                        name="customers"
                        data={filteredData}
                        columns={columns}
                        defaultSort={defaultSort}
                        onRowClicked={showDetails}
                        pagination={pagination}
                        setPagination={setPagination}
                        customActions={Filters}
                        isInitialLoading={isInitialLoading}
                    />
                </>
            )}
        </PageContainer>
    );
}
function CustomerListFilters({
    hideInternalCustomers,
    setHideInternalCustomers,
}: {
    hideInternalCustomers: boolean;
    setHideInternalCustomers: React.Dispatch<React.SetStateAction<boolean>>;
}) {
    return (
        <Box
            sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'row',
                paddingLeft: 5,
            }}
        >
            <Tooltip title="Internal customers are accounts used by staff and not external customers.">
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={hideInternalCustomers}
                            onChange={event => {
                                setHideInternalCustomers(event.target.checked);
                            }}
                            name="customerHideInternalCustomers"
                            data-testid="customerHideInternalCustomers"
                        />
                    }
                    label="Hide internal Customers"
                />
            </Tooltip>
        </Box>
    );
}

function CustomerListButtons() {
    const theme = useTheme();
    const handleExpiredSession = useHandleExpiredSession();
    const { enqueueSnackbar } = useSnackbar();
    const triggerDownloadCustomerOrigins = () => {
        downloadCustomerOrigins()
            .then(triggerDownload)
            .catch(error => {
                enqueueSnackbar('Downloading customer origin report failed', { variant: 'error' });
                handleExpiredSession(error);
            });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                gap: theme.spacing(2),
            }}
        >
            <RoleDependentComponent
                userRole={ROLE_DEFINITIONS.PrivilegedCustomer}
                component={ActionButton}
                color="primary"
                iconClass={ExcelIcon}
                title="Customer Origins"
                onClick={triggerDownloadCustomerOrigins}
            />
            <RoleDependentComponent
                userRole={ROLE_DEFINITIONS.PrivilegedCustomer}
                component={LinkButton}
                iconClass={CreateIcon}
                route={ROUTES.CUSTOMER_CREATE}
                title="Create"
                data-testid="FilterCustomers_CreateCustomer"
            />
        </Box>
    );
}
